import type { Content, Editor } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import * as React from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import { Separator } from '~shared/shadcn/ui/separator';

import { LinkBubbleMenu } from './components/bubble-menu/link-bubble-menu';
import { MeasuredContainer } from './components/measured-container';
import { SectionFive } from './components/section/five';
import { SectionFour } from './components/section/four';
import { SectionOne } from './components/section/one';
import { SectionThree } from './components/section/three';
import { SectionTwo } from './components/section/two';
import type { UseMinimalTiptapEditorProps } from './hooks/use-minimal-tiptap';
import { useMinimalTiptapEditor } from './hooks/use-minimal-tiptap';
import './styles/index.css';

export interface MinimalTiptapProps
  extends Omit<UseMinimalTiptapEditorProps, 'onUpdate'> {
  value?: Content;
  onChange?: (value: Content) => void;
  className?: string;
  editorContentClassName?: string;
  showImageUpload?: boolean;
}

const Toolbar = ({
  editor,
  showImageUpload = true,
}: {
  editor: Editor;
  showImageUpload?: boolean;
}) => (
  <div className='shrink-0 overflow-x-auto border-b border-border p-2'>
    <div className='flex w-max items-center gap-px'>
      <SectionOne editor={editor} activeLevels={[1, 2, 3, 4, 5, 6]} />

      <Separator orientation='vertical' className='mx-2 h-7' />

      <SectionTwo
        editor={editor}
        activeActions={[
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'code',
          'clearFormatting',
        ]}
        mainActionCount={3}
      />

      <Separator orientation='vertical' className='mx-2 h-7' />

      <SectionThree editor={editor} />

      <Separator orientation='vertical' className='mx-2 h-7' />

      <SectionFour
        editor={editor}
        activeActions={['orderedList', 'bulletList']}
        mainActionCount={0}
      />

      <Separator orientation='vertical' className='mx-2 h-7' />

      <SectionFive
        editor={editor}
        activeActions={['codeBlock', 'blockquote', 'horizontalRule']}
        mainActionCount={0}
        showImageUpload={showImageUpload}
      />
    </div>
  </div>
);

export const MinimalTiptapEditor = React.forwardRef<
  HTMLDivElement,
  MinimalTiptapProps
>(
  (
    {
      value,
      onChange,
      className,
      editorContentClassName,
      showImageUpload = true,
      ...props
    },
    ref,
  ) => {
    const editor = useMinimalTiptapEditor({
      value,
      onUpdate: onChange,
      ...props,
    });

    if (!editor) {
      return null;
    }

    return (
      <MeasuredContainer
        as='div'
        name='editor'
        ref={ref}
        className={cn(
          'flex h-auto min-h-72 w-full flex-col rounded-md border border-input shadow-sm focus-within:border-primary',
          className,
        )}
        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
          const target = event.target;
          if (!(target instanceof HTMLElement)) {
            return;
          }
          if (!target.closest('.minimal-tiptap-editor')) {
            return;
          }

          if (!editor.view.hasFocus()) {
            editor.view.focus();
            editor.commands.setTextSelection({
              from: editor.state.doc.nodeSize - 2,
              to: editor.state.doc.nodeSize - 2,
            });
          }
        }}
      >
        <Toolbar editor={editor} showImageUpload={showImageUpload} />
        <EditorContent
          editor={editor}
          className={cn('minimal-tiptap-editor flex-1', editorContentClassName)}
        />
        <LinkBubbleMenu editor={editor} />
      </MeasuredContainer>
    );
  },
);

MinimalTiptapEditor.displayName = 'MinimalTiptapEditor';

export default MinimalTiptapEditor;
